import {FocusKeys} from '@primer/behaviors'
import {SmileyIcon} from '@primer/octicons-react'
import {ActionList, AnchoredOverlay, Box, IconButton, useFocusZone} from '@primer/react'
import {useCallback, useState} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import type {ReactionViewerGroups$key} from './__generated__/ReactionViewerGroups.graphql'
import {ReactionButton} from './components/ReactionButton'
import {ReactionsMenuItem} from './components/ReactionsMenuItem'
import type {ReactionContent} from './mutations/__generated__/addReactionMutation.graphql'
import {addReactionMutation} from './mutations/add-reaction-mutation'
import {removeReactionMutation} from './mutations/remove-reaction-mutation'

const ReactionGroupsFragment = graphql`
  fragment ReactionViewerGroups on Reactable {
    reactionGroups {
      ...ReactionButton_Reaction
      ...ReactionsMenuItem_Reaction
    }
  }
`

export type ReactionViewerProps = {
  reactionGroups: ReactionViewerGroups$key
  subjectId: string
  locked?: boolean
}

export function ReactionViewer({reactionGroups, subjectId, locked = false}: ReactionViewerProps) {
  const environment = useRelayEnvironment()
  const reactionGroupsData = useFragment<ReactionViewerGroups$key>(ReactionGroupsFragment, reactionGroups)
  const [isOpen, setOpen] = useState(false)

  const onReact = useCallback(
    (subject: string, reaction: ReactionContent, viewerHasReacted: boolean) => {
      if (locked) return
      if (viewerHasReacted) {
        removeReactionMutation({
          environment,
          input: {subject, content: reaction},
        })
      } else {
        addReactionMutation({
          environment,
          input: {subject, content: reaction},
        })
      }
    },
    [environment, locked],
  )

  const {containerRef} = useFocusZone({
    bindKeys: FocusKeys.HomeAndEnd | FocusKeys.ArrowHorizontal,
    focusOutBehavior: 'wrap',
  })

  return (
    <Box
      role="toolbar"
      aria-label="Reactions"
      ref={containerRef as React.RefObject<HTMLDivElement>}
      sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}
    >
      {!locked && (
        <AnchoredOverlay
          open={isOpen}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          anchorRef={containerRef}
          focusZoneSettings={{
            bindKeys: FocusKeys.ArrowAll | FocusKeys.HomeAndEnd,
            focusOutBehavior: 'wrap',
          }}
          renderAnchor={p => (
            <IconButton
              size="small"
              sx={{
                borderRadius: 100,
                height: 28,
                width: 28,
                display: 'flex',
                alignItems: 'center',
                '& > span': {
                  lineHeight: 1,
                  ml: '-1px',
                },
              }}
              icon={SmileyIcon}
              {...p}
              aria-label="All reactions"
              aria-labelledby={undefined}
            />
          )}
        >
          <ActionList
            sx={{display: 'flex', flexDirection: 'row', p: 1, gap: 1}}
            role="menu"
            aria-orientation="horizontal"
          >
            {(reactionGroupsData.reactionGroups || []).map((reactionGroup, index) => (
              <ReactionsMenuItem
                key={index}
                reaction={reactionGroup}
                onReact={(reaction: ReactionContent, viewerHasReacted: boolean) => {
                  onReact(subjectId, reaction, viewerHasReacted)
                  setOpen(false)
                }}
              />
            ))}
          </ActionList>
        </AnchoredOverlay>
      )}

      {(reactionGroupsData.reactionGroups || []).map((reactionGroup, index) => (
        <ReactionButton
          reaction={reactionGroup}
          key={index}
          onReact={(reaction: ReactionContent, viewerHasReacted: boolean) =>
            onReact(subjectId, reaction, viewerHasReacted)
          }
        />
      ))}
    </Box>
  )
}

try{ ReactionViewer.displayName ||= 'ReactionViewer' } catch {}